import * as React from 'react';

import { useDispatch } from 'react-redux';

import { Grid, Link, Spacer, Typography } from '@emburse/ui-core';

import { StoreCompany } from '@Actions//company';

import { CompanyApi } from '@ClientApi/company';
import { ICompanyModel } from '@Shared/models';

import { SignUpWorkflowFooter } from '../SignUpWorkflowFooter';
import { SignUpImplementationDescriptions } from './SignUpImplementationDescriptions';

import * as style from './SignUpImplementationSelection.styl';

interface ISignUpImplementationSelectionProps {
  onImplementationSelection: (navDirection: 'next' | 'back') => void;
  onBack: (navDirection: 'next' | 'back') => void;
  onError: () => void;
}

export const SignUpImplementationSelection: React.FC<ISignUpImplementationSelectionProps> = ({
  onImplementationSelection,
  onBack,
  onError
}) => {
  const dispatch = useDispatch();
  const helpLink = window.whitelabelConfiguration?.supportLinks?.supportHome || 'https://help.spend.emburse.com/hc/en-us';
  const [guidedImplementationPrice, setGuidedImplementationPrice] = React.useState<string | null>(null);

  React.useEffect(() => {
    async function fetchGuidedImplementationPrice() {
      const data = await CompanyApi.getGuidedImplementationPrice();
      setGuidedImplementationPrice(data.formattedPrice);
    }

    fetchGuidedImplementationPrice();
  }, []);

  async function handleSelectImplementation(value: ICompanyModel['implementation_options']) {
    try {
      const updatedCompany = await CompanyApi.updateImplementationOptions(value);
      dispatch(new StoreCompany(updatedCompany));
      onImplementationSelection('next');
    } catch (error) {
      onError();
    }
  }

  return (
    <Grid container direction="column" spacing={1} sx={{ height: '100%', maxWidth: '754px' }} wrap="nowrap" className={style.global}>
      <Grid item className="layout" direction="column">
        <Grid item>
          <Typography variant="h4" style={{ alignSelf: 'flex-start' }}>
            How would you like to implement Emburse Spend?
          </Typography>
          <Spacer size={8} />
        </Grid>
        <Grid item>
          <Typography variant="subtitle1">Choose the option that works best for your organization</Typography>
          <Spacer size={32} />
        </Grid>
        <SignUpImplementationDescriptions
          handleSelectImplementation={handleSelectImplementation}
          guidedImplementationPrice={guidedImplementationPrice}
        />
        <Spacer size={24} />
        <Grid item>
          <Typography variant="subtitle1">
            Not sure which option would best suit your business? Check out our{' '}
            <Link href={helpLink} target="_blank" rel="noopener" className="imp-guide-help">
              implementation guide
            </Link>
            .
          </Typography>
        </Grid>
      </Grid>
      <Grid item xs>
        <Spacer size={4} />
      </Grid>
      <Spacer size={24} />
      <Grid item container xs alignContent="flex-end">
        <SignUpWorkflowFooter onBack={() => onBack('back')} showSupportLink={true} ariaLabel="implementation-selection" />
      </Grid>
    </Grid>
  );
};
